import { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import api from '../../services/api'
import { useAuthStore } from '../../store/authStore'
import StudentDashboard from './Dashboard'
import ProjectLabPortal from './ProjectLabPortal'
import LoadingSpinner from '../../components/LoadingSpinner'

/**
 * StudentExperienceGateway
 *
 * Contextual boundary between the academic student portal and the
 * Project Lab participant portal. Mounted by App.jsx for every /student/* path,
 * so it is the single place that decides which shell a student sees.
 *
 *   Academic enrollments (with or without Project Lab) → StudentDashboard
 *   Project Lab only                                   → ProjectLabPortal
 *   Neither                                            → ProjectLabPortal empty state
 *
 * The academic portal is rendered ONLY when GET /students/context positively
 * reports has_academic_enrollments === true. A failed context request is
 * never treated as "academic" — that previously dropped Project Lab-only
 * participants into the full Student Portal + course-selection modal whenever
 * the request failed (e.g. a 403 for an invalid/expired token, which the api
 * interceptor does not handle because it only reacts to 401).
 */
export default function StudentExperienceGateway() {
  const navigate = useNavigate()
  const userId = useAuthStore((s) => s.user?.id)
  const logout = useAuthStore((s) => s.logout)

  const [context, setContext] = useState(null)
  const [status, setStatus] = useState('loading') // loading | ready | error
  const [error, setError] = useState(null)
  const [attempt, setAttempt] = useState(0)

  const retry = useCallback(() => setAttempt((n) => n + 1), [])

  // useNavigate() returns a new function on every location change. Keep it in a
  // ref so in-portal navigation never re-triggers context resolution (which would
  // unmount the whole portal and every page inside it).
  const navigateRef = useRef(navigate)
  navigateRef.current = navigate

  useEffect(() => {
    let cancelled = false
    setStatus('loading')
    setError(null)
    setContext(null)

    api.get('/students/context')
      .then((res) => {
        if (cancelled) return
        setContext(res.data)
        setStatus('ready')
      })
      .catch((err) => {
        if (cancelled) return
        const code = err.response?.status
        console.error('[ExperienceGateway] Failed to load context:', code, err.response?.data || err.message)
        if (code === 401 || code === 403) {
          // Session is not valid for the student portal: end it cleanly instead
          // of guessing which portal to show.
          logout()
          navigateRef.current('/login', { replace: true })
          return
        }
        setError(err.response?.data?.error || 'We could not load your workspace.')
        setStatus('error')
      })

    return () => { cancelled = true }
    // Re-resolve whenever the signed-in identity changes.
  }, [userId, attempt, logout])

  if (status === 'loading') {
    return (
      <div className="cinema-page min-h-screen flex items-center justify-center">
        <LoadingSpinner label="Loading your workspace" />
      </div>
    )
  }

  if (status === 'error') {
    return (
      <div className="cinema-page min-h-screen flex items-center justify-center px-4">
        <div className="max-w-md w-full border-t border-white/10 pt-8">
          <span className="text-[10px] uppercase tracking-[0.2em] text-cinema-muted font-medium block mb-3">
            Synergy Multimedia Academy
          </span>
          <h1 className="font-display text-2xl text-white font-light">Workspace unavailable</h1>
          <p className="text-sm text-cinema-muted mt-3 leading-relaxed">{error}</p>
          <div className="flex items-center gap-6 mt-8">
            <button
              type="button"
              onClick={retry}
              className="px-5 py-2.5 bg-cinema-gold text-black text-sm font-medium rounded-none hover:bg-yellow-400 transition-colors"
            >
              Try again
            </button>
            <button
              type="button"
              onClick={() => { logout(); navigate('/login', { replace: true }) }}
              className="text-sm text-cinema-muted hover:text-white transition-colors"
            >
              Sign out
            </button>
          </div>
        </div>
      </div>
    )
  }

  if (context?.has_academic_enrollments === true) {
    return <StudentDashboard />
  }

  return (
    <ProjectLabPortal
      enrollments={context?.project_lab_enrollments || []}
      hasProjectLabEnrollments={context?.has_project_lab_enrollments === true}
    />
  )
}
